import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type IButton from 'components/Button/shared/types/button';
import type { ScrollSnapSlider } from 'scroll-snap-slider';
import useScrollSnapSliderContext from '../hooks/useScrollSnapSliderContext';
import NavigationButton from './NavigationButton';

interface INavigationButton extends IButton {
    navType: 'next' | 'prev';
    slideAmount?: number;
}

const StyledButton = styled(NavigationButton)<{ show: boolean }>`
    opacity: ${({ show }) => (show ? 1 : 0)};
    pointer-events: ${({ show }) => (show ? 'auto' : 'none')};
    transition: opacity 300ms ease;
`;

function useToggleNavigationButtons(slider?: ScrollSnapSlider) {
    const [showPrev, setShowPrev] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const observerRef = useRef<IntersectionObserver>();

    useEffect(() => {
        const rootElement = slider?.element;
        const firstSlide = slider?.element.firstElementChild;
        const lastSlide = slider?.element.lastElementChild;

        const callback: IntersectionObserverCallback = (entries) => {
            entries.forEach((entry) => {
                const { target, isIntersecting } = entry;

                if (target === firstSlide) {
                    setShowPrev(!isIntersecting);
                }

                if (target === lastSlide) {
                    setShowNext(!isIntersecting);
                }
            });
        };

        observerRef.current = new IntersectionObserver(callback, { root: rootElement, threshold: 0.8 });

        if (firstSlide && lastSlide) {
            observerRef.current.observe(firstSlide);
            observerRef.current.observe(lastSlide);
        }

        return () => {
            observerRef.current?.disconnect();
        };
    }, [slider]);

    return {
        showPrev,
        showNext
    };
}

export default function NavigationFadeButton({ navType, ...restProps }: INavigationButton) {
    const slider = useScrollSnapSliderContext();
    const { showPrev, showNext } = useToggleNavigationButtons(slider);

    return (
        <StyledButton
            type="button"
            show={(navType === 'prev' && showPrev) || (navType === 'next' && showNext)}
            navType={navType}
            tabIndex={-1}
            aria-hidden
            {...restProps}
        >
            {restProps.children}
        </StyledButton>
    );
}
